import React, { useRef, useEffect, useMemo } from 'react';
import ContextMenu, { ContextMenuAction } from './ContextMenu';

export type CardAction = {
    text: string;
    action: () => void;
};

// ─── Module-level constants — never recreated on render ──────────────────────
const BADGE_STYLE: React.CSSProperties = { position: 'relative', marginTop: '-1px', marginLeft: '-1px' };
const INFOBAR_HIGHLIGHT_STYLE: React.CSSProperties = { background: 'yellow', border: '.5px solid gray', borderRadius: '5%' };
const INFOBAR_STYLE: React.CSSProperties = { margin: '0' };
const RIGHT_BADGE_GAP: React.CSSProperties = { gap: '1rem' };
const EMPTY_STYLE: React.CSSProperties = {};
const ACTION_ITEM_STYLE: React.CSSProperties = { width: '14rem', minHeight: '2.5rem' };
// ─────────────────────────────────────────────────────────────────────────────

// ─── ListCard ─────────────────────────────────────────────────────────────────
// Zero hooks, zero refs, zero effects — just a styled div.
// Use this for any list of cards (search results, staffed list, etc.) where you
// don't need badge / context-menu / popup-actions features.
// The full Card below is reserved for feature-rich cards that actually use those.
export interface IListCardProps {
    className?: string;
    style?: React.CSSProperties;
    onClick?: (e: React.MouseEvent<HTMLDivElement>) => void;
    onDoubleClick?: (e: React.MouseEvent<HTMLDivElement>) => void;
    children?: React.ReactNode;
}

export const ListCard: React.FC<IListCardProps> = React.memo(({ className, style, onClick, onDoubleClick, children }) => {
    const cn = className ? `dlc-card ${className}` : 'dlc-card';
    return (
        <div className={cn} style={style} onClick={onClick} onDoubleClick={onDoubleClick}>
            {children}
        </div>
    );
});
// ─────────────────────────────────────────────────────────────────────────────

// ─── Full Card ────────────────────────────────────────────────────────────────
// Use when you need badge, infoBar, rightBadgePanel, contextMenu, or legacy
// actions popup. Has a useRef + useEffect for popup initialisation.
export interface ICardProps {
    style?: React.CSSProperties;
    rightBadgePanelstyle?: React.CSSProperties;
    divKey?: number;
    badge?: string;
    rightBadgePanel?: string | React.ReactElement;
    infoBar?: string | React.ReactElement;
    actions?: CardAction[];
    actionPanelStyle?: React.CSSProperties;
    contextActions?: ContextMenuAction[];
    onClick?: (e: React.MouseEvent<HTMLDivElement, MouseEvent>) => void;
    onDoubleClick?: (e: React.MouseEvent<HTMLDivElement, MouseEvent>) => void;
    children?: React.ReactElement[] | React.ReactElement;
    className?: string;
    newContext?: boolean;
    isHidden?: boolean;
    showMenu?: boolean;
    isHighlighted?: boolean;
    collapse?: boolean;
}

const Card: React.FC<ICardProps> = (props) => {
    const popupEl = useRef<HTMLDivElement>(null);

    const cardClassName = useMemo(() => {
        let cn = 'dlc-card';
        if (props.badge || props.rightBadgePanel) { cn += ' has-badge'; }
        if ((props.actions && props.actions.length > 0) || (props.contextActions && props.contextActions.length > 0)) { cn += ' has-actions'; }
        if (props.className) { cn += ' ' + props.className; }
        return cn;
    }, [props.badge, props.rightBadgePanel, props.actions, props.contextActions, props.className]);

    // Only runs for cards that actually render the popup panel (actions prop present)
    useEffect(() => {
        if (popupEl.current) {
            const el = popupEl.current;
            el.style.height = '2rem';
            el.style.width = '1.7rem';
            el.style.opacity = '0';
            el.style.pointerEvents = 'none';
        }
    }, []);

    const clickAction = (event: React.MouseEvent<HTMLDivElement>, action: CardAction) => {
        event.stopPropagation();
        action.action();
    };

    const togglePopup = (state: boolean) => {
        if (props.actions && props.actions.length > 0 && popupEl.current) {
            const el = popupEl.current;
            el.style.transition = 'opacity 0.2s';
            if (state) {
                el.style.height = 'auto';
                el.style.width = 'auto';
                el.style.opacity = '1';
                el.style.pointerEvents = 'inherit';
            } else {
                el.style.height = '2rem';
                el.style.width = '1.7rem';
                el.style.opacity = '0';
                el.style.pointerEvents = 'none';
            }
        }
    };

    const cardStyle = useMemo(
        () => ({ ...props.style, display: props.isHidden ? 'none' : 'block' }),
        [props.style, props.isHidden]
    );

    const hasHeader = !!(
        props.badge || props.rightBadgePanel || props.infoBar
        || (props.newContext && props.showMenu)
        || (!props.newContext && props.actions && props.actions.length > 0)
    );

    return (
        <div
            key={props.divKey}
            onDoubleClick={props.onDoubleClick}
            onClick={props.onClick}
            className={cardClassName}
            style={cardStyle}
        >
            {hasHeader && (
                <div className="card-header-row">
                    <div className="left-info-badge">
                        <div className="badge">
                            {props.badge && (
                                <p className="badge-content" style={BADGE_STYLE}>
                                    {props.badge}
                                </p>
                            )}
                        </div>
                        <div className="info-bar">
                            {props.infoBar && props.isHighlighted && (
                                <span style={INFOBAR_HIGHLIGHT_STYLE}>&nbsp;{props.infoBar}&nbsp;</span>
                            )}
                            {props.infoBar && !props.isHighlighted && (
                                <span style={INFOBAR_STYLE}>{props.infoBar}</span>
                            )}
                        </div>
                    </div>
                    <div className="right-info-badge" style={RIGHT_BADGE_GAP}>
                        {props.rightBadgePanel && (
                            <span
                                style={props.rightBadgePanelstyle || EMPTY_STYLE}
                                className="right-info-badge-content"
                            >
                                {props.rightBadgePanel}
                            </span>
                        )}
                        {!!props.newContext && !!props.showMenu && (
                            <ContextMenu isVisible={props.showMenu} actions={props.contextActions} />
                        )}
                    </div>
                </div>
            )}
            {!props.collapse && props.children && <br />}
            {props.children}
            {!props.newContext && props.actions && props.actions.length > 0 && (
                <>
                    <div style={props.actionPanelStyle} className="actions-panel">
                        <span className="options-button" onMouseOver={() => togglePopup(true)} />
                    </div>
                    <div className="actions-popup-mask" ref={popupEl} onMouseLeave={() => togglePopup(false)}>
                        <div className="actions-popup">
                            <div className="actions-popup-header">Options</div>
                            {props.actions.map((action, index) => (
                                <div
                                    className="action"
                                    key={index}
                                    onClick={(e) => clickAction(e, action)}
                                    style={ACTION_ITEM_STYLE}
                                >
                                    {action.text}
                                </div>
                            ))}
                        </div>
                    </div>
                </>
            )}
        </div>
    );
};

Card.defaultProps = {
    newContext: false,
    isHidden: false,
};

export default React.memo(Card);
// ─────────────────────────────────────────────────────────────────────────────



// WEBPACK FOOTER //
// ./src/components/common/Card.redesign.tsx